import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HTTP } from '../../api/http-common';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { FaEye, FaSave, FaSearch, FaSignOutAlt } from 'react-icons/fa';
import Loader from '../../components/loader';
import './ViewCountManagement.css';
import useUserRoleEffect from '../../components/userRoleEffect';

export default function ViewCountManagement() {
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [videos, setVideos] = useState([]);
    const [filteredVideos, setFilteredVideos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editedCounts, setEditedCounts] = useState({});
    const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
    
    const userObj = JSON.parse(localStorage.getItem("userObj"));
    const userRole = userObj?.role?.id;
    useUserRoleEffect(userRole);

    useEffect(() => {
        // Store the intended destination
        localStorage.setItem('redirectAfterLogin', '/admin/view-counts');
        checkAuthentication();
    }, []);

    useEffect(() => {
        // Filter videos based on search term
        if (searchTerm === '') {
            setFilteredVideos(videos);
        } else {
            const filtered = videos.filter(video =>
                video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                video.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredVideos(filtered);
        }
    }, [searchTerm, videos]);

    const checkAuthentication = async () => {
        const access_token = localStorage.getItem('access_token');

        if (!access_token) {
            // Not logged in - redirect to main login
            setIsLoading(false);
            navigate('/');
            return;
        }

        try {
            const config = {
                headers: { 
                    'Content-Type': 'application/json', 
                    'token': access_token 
                },
            };

            // Check if user is authenticated
            const userResponse = await HTTP.get("user/me", config);

            if (userResponse.status === 200) {
                const userObj = userResponse.data;
                const userRole = userObj?.role?.name;

                // Check if user is Admin
                if (userRole === 'Admin') {
                    setIsAuthenticated(true);
                    setIsAdmin(true);
                    setAccessToken(access_token);
                    // Clear redirect flag
                    localStorage.removeItem('redirectAfterLogin');
                    // Load videos
                    loadAllVideos(access_token);
                } else {
                    Swal.fire('Access Denied', 'Only Admin users can access this page', 'error');
                    setIsLoading(false);
                    setTimeout(() => {
                        navigate('/content/list');
                    }, 2000);
                }
            }
        } catch (error) {
            console.log('Authentication error:', error);
            setIsLoading(false);
            // Redirect to login
            navigate('/');
        }
    };

    const loadAllVideos = async (token) => {
        try {
            setIsLoading(true);
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token || accessToken
                },
            };

            const response = await HTTP.get('/content/view-counts/list', config);

            if (response.status === 200) {
                setVideos(response.data.videos);
                setFilteredVideos(response.data.videos);
            }
        } catch (err) {
            console.error('Error loading videos:', err);
            Swal.fire('Error', 'Failed to load videos', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewCountChange = (videoId, value) => {
        setEditedCounts(prev => ({
            ...prev,
            [videoId]: value
        }));
    };

    const handleUpdateViewCount = async (video) => {
        const videoId = video.id;
        const newCount = editedCounts[videoId];

        if (newCount === undefined || newCount === '') {
            Swal.fire('Error', 'Please enter a view count', 'error');
            return;
        }

        if (parseInt(newCount) < 0) {
            Swal.fire('Error', 'View count cannot be negative', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'token': accessToken
                },
            };

            const formData = new FormData();
            formData.append('view_count', newCount);

            const response = await HTTP.put(`/content/view-counts/update/${videoId}`, formData, config);

            if (response.status === 200) {
                Swal.fire('Success', 'View count updated successfully', 'success');
                
                setVideos(prevVideos =>
                    prevVideos.map(v =>
                        v.id === videoId ? { ...v, view_count: parseInt(newCount) } : v
                    )
                );

                setEditedCounts(prev => {
                    const newEdited = { ...prev };
                    delete newEdited[videoId];
                    return newEdited;
                });
            }
        } catch (err) {
            Swal.fire('Error', err.response?.data?.detail || 'Failed to update view count', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkUpdate = async () => {
        const updates = Object.entries(editedCounts).map(([videoId, viewCount]) => ({
            video_id: parseInt(videoId),
            view_count: parseInt(viewCount)
        }));

        if (updates.length === 0) {
            Swal.fire('Info', 'No changes to update', 'info');
            return;
        }

        try {
            setIsLoading(true);
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    'token': accessToken
                },
            };

            const response = await HTTP.put('/content/view-counts/bulk-update', updates, config);

            if (response.status === 200) {
                Swal.fire(
                    'Bulk Update Complete',
                    `Updated: ${response.data.updated_count}, Failed: ${response.data.failed_count}`,
                    'success'
                );
                
                loadAllVideos();
                setEditedCounts({});
            }
        } catch (err) {
            Swal.fire('Error', 'Failed to perform bulk update', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('userObj');
        localStorage.removeItem('access_token_expires');
        localStorage.removeItem('redirectAfterLogin');
        setAccessToken(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
        navigate('/');
    };

    const getStatusBadgeClass = (statusId) => {
        switch (statusId) {
            case 1: return 'status-draft';
            case 2: return 'status-approved';
            case 3: return 'status-published';
            case 4: return 'status-unpublished';
            default: return '';
        }
    };

    // LOADING STATE
    if (isLoading) {
        return <Loader />;
    }

    // AUTHENTICATED AND ADMIN - SHOW MANAGEMENT INTERFACE
    return (
        <div className="standalone-management-page">
            {/* Header */}
            <div className="standalone-header">
                <div className="header-left">
                    <img src="/images/logo/gsa.svg" alt="GSA Connect" className="header-logo" />
                    <div>
                        <h1>View Count Management</h1>
                        <p>Manage video view counts</p>
                    </div>
                </div>
                <div className="header-right">
                    {Object.keys(editedCounts).length > 0 && (
                        <button
                            className="btn btn-success"
                            onClick={handleBulkUpdate}
                            disabled={isLoading}
                        >
                            <FaSave /> Save All ({Object.keys(editedCounts).length})
                        </button>
                    )}
                    <button className="btn btn-logout" onClick={handleLogout}>
                        <FaSignOutAlt /> Logout
                    </button>
                </div>
            </div>

            {/* Search Section */}
            <div className="search-section">
                <div className="search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search videos by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="total-count">
                    Total: {filteredVideos.length} videos
                </div>
            </div>

            {/* Videos List */}
            <div className="videos-list">
                {filteredVideos && filteredVideos.length > 0 ? (
                    filteredVideos.map((video) => (
                        <div key={video.id} className="video-item">
                            <div className="video-info">
                                <div className="video-poster">
                                    {video.poster_url ? (
                                        <img
                                            src={video.poster_url}
                                            alt={video.title}
                                            onError={(e) => {
                                                e.target.src = '/images/logo/gsa.svg';
                                            }}
                                        />
                                    ) : (
                                        <div className="no-poster">
                                            <FaEye />
                                        </div>
                                    )}
                                </div>
                                
                                <div className="video-details">
                                    <h3>{video.title}</h3>
                                    <div className="video-meta">
                                        <span className={`badge ${getStatusBadgeClass(video.status_id)}`}>
                                            {video.status}
                                        </span>
                                        {video.section_name && (
                                            <span className="badge badge-section">
                                                {video.section_name}
                                            </span>
                                        )}
                                        {video.created_by_name && (
                                            <span className="created-by">
                                                By: {video.created_by_name}
                                            </span>
                                        )}
                                    </div>
                                    {video.description && (
                                        <p className="video-desc">
                                            {video.description.length > 120
                                                ? `${video.description.substring(0, 120)}...`
                                                : video.description}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="view-count-section">
                                <label>
                                    <FaEye /> View Count
                                </label>
                                <div className="count-controls">
                                    <input
                                        type="number"
                                        className="count-input"
                                        value={
                                            editedCounts[video.id] !== undefined
                                                ? editedCounts[video.id]
                                                : video.view_count
                                        }
                                        onChange={(e) =>
                                            handleViewCountChange(video.id, e.target.value)
                                        }
                                        min="0"
                                    />
                                    <button
                                        className="btn-update"
                                        onClick={() => handleUpdateViewCount(video)}
                                        disabled={editedCounts[video.id] === undefined || isLoading}
                                    >
                                        <FaSave /> Update
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-videos">
                        <p>No videos found</p>
                    </div>
                )}
            </div>
        </div>
    );
}